// @owners { team: patients-team }
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { fetchCheckoutNextAvailableDates } from '~shared/features/next-available-date/queries/fetchCheckoutNextAvailableDates';
import { useDispatchShared, useStore } from '~shared/store';
import { type ReduxStateShared } from '~shared/types';

export const useFetchNextAvailableDatesForCart = () => {
  const dispatch = useDispatchShared();
  const queryClient = useQueryClient();
  const store = useStore();
  const { prescriptionIDs } = usePrescriptionsInCart();
  const addressID = useNextAvailableDateAddressId();

  return useCallback(
    async (options?: { unbundledPrescriptionIDs?: number[]; context?: string }) => {
      const unbundledPrescriptionIDs = options?.unbundledPrescriptionIDs || [];
      // on app start do not fetch next available dates when no prescriptions are in the cart
      if (unbundledPrescriptionIDs.length === 0 && prescriptionIDs.length === 0) {
        return;
      }

      await fetchCheckoutNextAvailableDates(queryClient, dispatch, () => store.getState() as ReduxStateShared, {
        unbundledPrescriptionIDs,
        bundledPrescriptionIDs: prescriptionIDs,
        addressID,
      });
    },
    [addressID, dispatch, prescriptionIDs, queryClient, store],
  );
};
